import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { inventory } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import {
  Package, AlertTriangle, AlertOctagon, TrendingUp,
  Layers, Palette, Ruler, ArrowDownUp, ShoppingBag,
  IndianRupee, BarChart3, Filter
} from 'lucide-react';

const CHART_COLORS = ['#4f7df9', '#2dd4a0', '#f5a623', '#9d7aff', '#ef5a6f', '#5be5d5', '#ff8a65', '#7986cb', '#66bb6a', '#ab47bc', '#29b6f6', '#ec407a'];

const ACTION_CONFIG: Record<string, { label: string; color: string }> = {
  deducted: { label: 'Order Deduction', color: 'var(--accent-red)' },
  rto_used: { label: 'RTO Used', color: 'var(--accent-purple)' },
  rto_received: { label: 'RTO Received', color: 'var(--accent-amber)' },
  restocked: { label: 'Restocked', color: 'var(--accent-green)' },
  manual: { label: 'Manual', color: 'var(--accent-blue)' },
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)', padding: '10px 14px',
      boxShadow: 'var(--shadow-elevated)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ fontSize: 13, fontWeight: 600, color: p.color || 'var(--text-primary)' }}>
          {p.name}: {typeof p.value === 'number' && p.name?.toLowerCase().includes('value')
            ? `₹${p.value.toLocaleString('en-IN')}`
            : p.value?.toLocaleString('en-IN')}
        </div>
      ))}
    </div>
  );
};

const InventoryAnalytics: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [activeTab, setActiveTab] = useState<'qty' | 'value'>('qty');

  useEffect(() => {
    setLoading(true);
    inventory.getAnalytics()
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout title="Inventory Analytics" subtitle="Loading...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: 'var(--text-muted)', fontSize: 14 }}>
          Loading analytics...
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout title="Inventory Analytics">
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', fontSize: 14 }}>
          Unable to load inventory analytics.
        </div>
      </Layout>
    );
  }

  const { kpis, category_breakdown, size_breakdown, color_breakdown, top_by_qty, top_by_value, low_stock_items, movement_history, movement_summary } = data;

  // Apply category filter
  const filteredLowStock = catFilter
    ? low_stock_items.filter((i: any) => i.category === catFilter)
    : low_stock_items;

  const filteredTopQty = catFilter
    ? top_by_qty.filter((i: any) => i.category === catFilter)
    : top_by_qty;

  const filteredTopValue = catFilter
    ? top_by_value.filter((i: any) => i.category === catFilter)
    : top_by_value;

  const catNames = category_breakdown.map((c: any) => c.category);

  const formatTime = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  return (
    <Layout title="Inventory Analytics" subtitle={`${kpis.total_skus} SKUs · ₹${kpis.total_value.toLocaleString('en-IN')} total value`}>
      {/* Category Filter */}
      <div className="animate-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={14} color="var(--accent-blue)" />
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>FILTER:</span>
          <button
            onClick={() => setCatFilter('')}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${!catFilter ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
              background: !catFilter ? 'var(--accent-blue-soft)' : 'var(--bg-card)',
              color: !catFilter ? 'var(--accent-blue)' : 'var(--text-secondary)',
              transition: 'var(--transition)',
            }}
          >All</button>
          {catNames.map((name: string) => (
            <button
              key={name}
              onClick={() => setCatFilter(name === catFilter ? '' : name)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${catFilter === name ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
                background: catFilter === name ? 'var(--accent-blue-soft)' : 'var(--bg-card)',
                color: catFilter === name ? 'var(--accent-blue)' : 'var(--text-secondary)',
                transition: 'var(--transition)',
              }}
            >{name}</button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="animate-in-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 22 }}>
        {[
          { label: 'Total SKUs', value: kpis.total_skus, icon: <Layers size={16} />, color: 'var(--text-primary)', soft: 'var(--bg-elevated)' },
          { label: 'Total Qty', value: kpis.total_qty.toLocaleString('en-IN'), icon: <Package size={16} />, color: 'var(--accent-blue)', soft: 'var(--accent-blue-soft)' },
          { label: 'Stock Value', value: `₹${kpis.total_value.toLocaleString('en-IN')}`, icon: <IndianRupee size={16} />, color: 'var(--accent-green)', soft: 'var(--accent-green-soft)' },
          { label: 'Avg Cost/pc', value: `₹${kpis.avg_cost.toLocaleString('en-IN')}`, icon: <TrendingUp size={16} />, color: 'var(--accent-amber)', soft: 'var(--accent-amber-soft)' },
          { label: 'Low Stock', value: kpis.low_stock_count, icon: <AlertTriangle size={16} />, color: 'var(--accent-amber)', soft: 'var(--accent-amber-soft)' },
          { label: 'Out of Stock', value: kpis.out_of_stock_count, icon: <AlertOctagon size={16} />, color: 'var(--accent-red)', soft: 'var(--accent-red-soft)' },
        ].map((k, i) => (
          <div key={i} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: '16px 18px',
          }}>
            <div style={{ color: k.color, marginBottom: 10, width: 32, height: 32, borderRadius: 'var(--radius-sm)', background: k.soft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{k.icon}</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>{k.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: k.color, letterSpacing: -0.5 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Charts Row: Category + Size + Color */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 16, marginBottom: 22 }}>
        {/* Category Breakdown Bar Chart */}
        <div className="animate-in-2" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Layers size={15} color="var(--accent-blue)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>By Category</span>
          </div>
          {category_breakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={category_breakdown} layout="vertical" margin={{ left: 10, right: 10 }}>
                <XAxis type="number" tick={{ fill: '#4d506b', fontSize: 10 }} axisLine={false} tickLine={false}
                  tickFormatter={v => activeTab === 'value' ? `₹${(v/1000).toFixed(0)}k` : v.toString()} />
                <YAxis type="category" dataKey="category" tick={{ fill: '#7a7d9e', fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey={activeTab === 'value' ? 'value' : 'qty'} name={activeTab === 'value' ? 'Value' : 'Quantity'} radius={[0, 4, 4, 0]}>
                  {category_breakdown.map((_: any, i: number) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No data</div>
          )}
          {/* Toggle qty/value */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 8 }}>
            {(['qty', 'value'] as const).map(t => (
              <button key={t} onClick={() => setActiveTab(t)} style={{
                padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${activeTab === t ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
                background: activeTab === t ? 'var(--accent-blue-soft)' : 'transparent',
                color: activeTab === t ? 'var(--accent-blue)' : 'var(--text-muted)',
              }}>{t === 'qty' ? 'Quantity' : 'Value (₹)'}</button>
            ))}
          </div>
        </div>

        {/* Size Pie Chart */}
        <div className="animate-in-2" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Ruler size={15} color="var(--accent-purple)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>By Size</span>
          </div>
          {size_breakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={size_breakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={68}
                    dataKey="qty" nameKey="size" stroke="none" paddingAngle={3}>
                    {size_breakdown.map((_: any, i: number) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 6 }}>
                {size_breakdown.slice(0, 8).map((s: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{s.size}</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{s.qty}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No data</div>
          )}
        </div>

        {/* Color Pie Chart */}
        <div className="animate-in-2" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Palette size={15} color="var(--accent-amber)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>By Color</span>
          </div>
          {color_breakdown.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={color_breakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={68}
                    dataKey="qty" nameKey="color" stroke="none" paddingAngle={3}>
                    {color_breakdown.map((_: any, i: number) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 6 }}>
                {color_breakdown.slice(0, 8).map((c: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{c.color}</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{c.qty}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No data</div>
          )}
        </div>
      </div>

      {/* Category Detail Table */}
      <div className="animate-in-3" style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 22,
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart3 size={15} color="var(--accent-blue)" />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Category Overview</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Category', 'SKUs', 'Total Qty', 'Stock Value', 'Low Stock', 'Out of Stock', '% of Value'].map(h => (
                <th key={h} style={{ padding: '10px 18px', textAlign: h === 'Category' ? 'left' : 'right', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {category_breakdown.map((c: any, i: number) => {
              const pct = kpis.total_value > 0 ? ((c.value / kpis.total_value) * 100).toFixed(1) : '0';
              return (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                  <td style={{ padding: '12px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.category}</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 13, color: 'var(--text-secondary)' }}>{c.skus}</td>
                  <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--accent-blue)' }}>{c.qty.toLocaleString('en-IN')}</td>
                  <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--accent-green)' }}>₹{c.value.toLocaleString('en-IN')}</td>
                  <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                    {c.low > 0 ? (
                      <span style={{ fontSize: 11.5, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: 'var(--accent-amber-soft)', color: 'var(--accent-amber)' }}>{c.low}</span>
                    ) : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                    {c.out > 0 ? (
                      <span style={{ fontSize: 11.5, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: 'var(--accent-red-soft)', color: 'var(--accent-red)' }}>{c.out}</span>
                    ) : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                      <div style={{ width: 50, background: 'var(--bg-elevated)', borderRadius: 3, height: 5, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: CHART_COLORS[i % CHART_COLORS.length], borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, minWidth: 35 }}>{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {category_breakdown.length === 0 && <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No category data</div>}
      </div>

      {/* Bottom Row: Top Items + Low Stock + Movement */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 22 }}>
        {/* Top Items */}
        <div className="animate-in-3" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', overflow: 'hidden',
        }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShoppingBag size={15} color="var(--accent-green)" />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Top Items</span>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['qty', 'value'] as const).map(t => (
                <button key={t} onClick={() => setActiveTab(t)} style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${activeTab === t ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
                  background: activeTab === t ? 'var(--accent-blue-soft)' : 'transparent',
                  color: activeTab === t ? 'var(--accent-blue)' : 'var(--text-muted)',
                }}>{t === 'qty' ? 'By Qty' : 'By Value'}</button>
              ))}
            </div>
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
                  {['#', 'Item', 'Size', 'Qty', 'Value'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Item' ? 'left' : (h === '#' ? 'center' : 'right'), fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'qty' ? filteredTopQty : filteredTopValue).map((item: any, i: number) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 6, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 700,
                        background: i < 3 ? 'var(--accent-blue-soft)' : 'var(--bg-elevated)',
                        color: i < 3 ? 'var(--accent-blue)' : 'var(--text-muted)',
                      }}>{i + 1}</span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-primary)' }}>{item.color} {item.article_type}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 1 }}>{item.category}</div>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <span style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>{item.size}</span>
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--accent-blue)' }}>{item.quantity}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--accent-green)' }}>₹{item.value.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(activeTab === 'qty' ? filteredTopQty : filteredTopValue).length === 0 && (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>No items</div>
            )}
          </div>
        </div>

        {/* Low Stock & Out of Stock */}
        <div className="animate-in-4" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', overflow: 'hidden',
        }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={15} color="var(--accent-red)" />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Low & Out of Stock</span>
            </div>
            <span style={{
              fontSize: 10.5, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
              background: filteredLowStock.length > 0 ? 'var(--accent-red-soft)' : 'var(--accent-green-soft)',
              color: filteredLowStock.length > 0 ? 'var(--accent-red)' : 'var(--accent-green)',
            }}>{filteredLowStock.length > 0 ? `${filteredLowStock.length} alerts` : 'All clear'}</span>
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {filteredLowStock.length > 0 ? filteredLowStock.map((item: any) => (
              <div key={item.id} style={{
                padding: '12px 20px', borderBottom: '1px solid var(--border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {item.color} {item.article_type}
                    <span style={{ marginLeft: 8, background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '1px 7px', borderRadius: 5, fontSize: 10.5, fontWeight: 600 }}>{item.size}</span>
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3 }}>
                    {item.category} · Min: {item.notify_below} · ₹{item.cost_price}/pc
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: 18, fontWeight: 800, letterSpacing: -0.5,
                    color: item.quantity <= 0 ? 'var(--accent-red)' : 'var(--accent-amber)',
                  }}>{item.quantity}</div>
                  <div style={{
                    fontSize: 9.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2,
                    color: item.quantity <= 0 ? 'var(--accent-red)' : 'var(--accent-amber)',
                  }}>{item.quantity <= 0 ? 'OUT' : 'LOW'}</div>
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--accent-green)', fontSize: 13 }}>
                All stock levels are healthy.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stock Movement History */}
      <div className="animate-in-4" style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ArrowDownUp size={15} color="var(--accent-purple)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Stock Movement History</span>
          </div>
          {/* Movement summary pills */}
          <div style={{ display: 'flex', gap: 8 }}>
            {Object.entries(movement_summary).map(([action, info]: any) => {
              const cfg = ACTION_CONFIG[action] || { label: action, color: 'var(--text-muted)' };
              return (
                <div key={action} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  background: `${cfg.color}15`, color: cfg.color,
                }}>
                  <span>{cfg.label}</span>
                  <span style={{ opacity: 0.7 }}>×{info.total_qty}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
                {['Time', 'Action', 'Item', 'Size', 'Qty', 'Order', 'Notes'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {movement_history.map((log: any) => {
                const cfg = ACTION_CONFIG[log.action] || { label: log.action, color: 'var(--text-muted)' };
                return (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatTime(log.created_at)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        fontSize: 10.5, padding: '3px 9px', borderRadius: 20, fontWeight: 600,
                        background: `${cfg.color}15`, color: cfg.color,
                      }}>{cfg.label}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 500 }}>
                      {log.color} {log.article_type}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '2px 7px', borderRadius: 5, fontSize: 10.5, fontWeight: 600 }}>{log.size}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: log.action === 'deducted' ? 'var(--accent-red)' : 'var(--accent-green)' }}>
                      {log.action === 'deducted' ? '-' : '+'}{log.quantity}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--accent-blue)', fontWeight: 500 }}>{log.order_name || '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: 11.5, color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.notes || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {movement_history.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No stock movements recorded yet.</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default InventoryAnalytics;
